import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { DraftedAthletesTable, RosteredAthletesTable } from "./components/AthleteTables";
import { useAdminDashboardData } from "./hooks/useAdminDashboardData";
import {
  Plus, Mail, User, X, BarChart2, Shield, UserPlus,
  Trash2, ChevronDown, Users, Clock, MoreVertical
} from "lucide-react";

const parseCsvRows = (text) => {
  const rows = [];
  let current = "";
  let row = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (char === "\"") {
      if (inQuotes && next === "\"") {
        current += "\"";
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(current);
      current = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(current);
      if (row.some((value) => String(value).trim() !== "")) {
        rows.push(row);
      }
      row = [];
      current = "";
      continue;
    }

    current += char;
  }

  row.push(current);
  if (row.some((value) => String(value).trim() !== "")) {
    rows.push(row);
  }

  if (rows.length === 0) return [];

  const [headerRow, ...dataRows] = rows;
  return dataRows.map((dataRow) => {
    const record = {};
    headerRow.forEach((header, index) => {
      record[String(header || "").trim()] = String(dataRow[index] || "").trim();
    });
    return record;
  });
};

export default function AdminDashboard() {
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const normalizeEmail = (value) => (value || "").trim().toLowerCase();
const getRosterImportStatusMeta = (status) => {
  if (status === "invited") return { label: "Invited", className: "bg-blue-100 text-blue-700" };
  return null;
};

const abbreviatePositionLabel = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  if (normalized.length <= 4 && normalized === normalized.toUpperCase()) return normalized;
  const parts = normalized.split(/[\s/-]+/).filter(Boolean);
  if (parts.length > 1) {
    return parts.map((part) => part.charAt(0).toUpperCase()).join("");
  }
  return normalized.slice(0, 3).toUpperCase();
};
  const clampReadinessPercent = (userScore, targetScore) => {
    const user = Number(userScore);
    const target = Number(targetScore);
    if (!Number.isFinite(user) || !Number.isFinite(target) || target <= 0) return 0;
    return Math.max(0, Math.min((user / target) * 100, 100));
  };

  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- STATE: Programs & Teams ---
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");

  // --- STATE: Data ---
  const [viewingScores, setViewingScores] = useState(null); // <--- ADD THIS LINE

  // --- STATE: UI / Modals ---
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [openTeamActionMenu, setOpenTeamActionMenu] = useState(false);
  const [openProgramActionMenuId, setOpenProgramActionMenuId] = useState(null);
  const [showCoachInviteModal, setShowCoachInviteModal] = useState(false);
  const [coachInviteForm, setCoachInviteForm] = useState({ email: "", role: "Assistant Coach", team_id: "" });
  const [showAthleteInviteModal, setShowAthleteInviteModal] = useState(false);
  const [athleteInviteForm, setAthleteInviteForm] = useState({ email: "", team_id: "" });
  const [showPrebuildSwotModal, setShowPrebuildSwotModal] = useState(false);
  const [prebuildSwotForm, setPrebuildSwotForm] = useState({ name: "", email: "" });
  const [prebuildSwotErrors, setPrebuildSwotErrors] = useState({ name: "", email: "" });
  const [editingRosterContact, setEditingRosterContact] = useState(null);
  const [rosterContactForm, setRosterContactForm] = useState({ name: "", email: "" });
  const [rosterContactErrors, setRosterContactErrors] = useState({ name: "", email: "" });
  const [rosterView, setRosterView] = useState("rostered");
  const [refreshKey, setRefreshKey] = useState(0);
  const rosterCsvInputRef = useRef(null);
  const [playerTypeSaving, setPlayerTypeSaving] = useState({});
  const [positionSaving, setPositionSaving] = useState({});
  const [csvImportBusy, setCsvImportBusy] = useState(false);
  const [csvImportMessage, setCsvImportMessage] = useState("");
  const [csvImportError, setCsvImportError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { programId: urlProgramId, teamId: urlTeamId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const queryProgramId = searchParams.get("programId");
  const queryTeamId = searchParams.get("teamId");

  // Helper: Get Current Program Object
  const currentProgram = programs.find(p => String(p.id) === String(selectedProgramId));
  const currentTeam = teams.find(t => String(t.id) === String(selectedTeamId));
  const {
    athletes,
    setAthletes,
    draftEvaluations,
    setDraftEvaluations,
    coaches,
    setCoaches,
    programSportId,
    playerTypeOptions,
    playerTypeByAthlete,
    setPlayerTypeByAthlete,
    secondaryPlayerTypeByAthletePosition,
    athletePositionsByAthlete,
    athletePositionIdsByAthlete,
    positionOptions,
    playerTypeDraftByAthlete,
    setPlayerTypeDraftByAthlete,
    swotStatusByAthlete
  } = useAdminDashboardData({
    selectedProgramId,
    selectedTeamId,
    currentProgramSport: currentProgram?.sport,
    refreshKey
  });
  const getAthletePositionLabel = (athlete) => {
    const athleteKey = getAthletePlayerTypeKey(athlete);
    return String(athletePositionsByAthlete[athleteKey] || abbreviatePositionLabel(athlete?.position)).trim();
  };
  const renderTeamActionMenu = (onClose) => (
    <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
      <button
        type="button"
        onClick={() => {
          onClose();
          rosterCsvInputRef.current?.click();
        }}
        disabled={!selectedProgramId || !selectedTeamId || csvImportBusy}
        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-white"
      >
        {csvImportBusy ? "Importing..." : "Roster Import"}
      </button>
      <button
        type="button"
        onClick={() => {
          onClose();
          handleDownloadRosterTemplate();
        }}
        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        Download Roster Template
      </button>
      <button
        type="button"
        onClick={() => {
          onClose();
          handleEditTeam();
        }}
        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        Edit Team Information
      </button>
    </div>
  );

  useEffect(() => {
    setOpenTeamActionMenu(false);
    setOpenProgramActionMenuId(null);
  }, [selectedProgramId, selectedTeamId]);

  // ------------------------------------------------------------------
  // 1. INITIAL SETUP
  // ------------------------------------------------------------------
  useEffect(() => {
    const fetchUserAndPrograms = async () => {
      setLoading(true);
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) {
          setLoading(false);
          return;
        }
        setUser(authUser);

        const { data: userData } = await supabase
          .from("users")
          .select("role")
          .eq("id", authUser.id)
          .single();
        if (userData) setUserRole(userData.role);

        const { data: adminPrograms } = await supabase
          .from("programs")
          .select("*")
          .eq("administrator", authUser.id);

        setPrograms(adminPrograms || []);

        if (urlProgramId) {
          setSelectedProgramId(urlProgramId);
        } else if (queryProgramId) {
          setSelectedProgramId(queryProgramId);
        } else if (adminPrograms?.length > 0) {
          setSelectedProgramId(adminPrograms[0].id);
        }

      } catch (err) {
        console.error("Initialization error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserAndPrograms();
  }, [urlProgramId, queryProgramId]);

  // ------------------------------------------------------------------
  // 2. FETCH TEAMS
  // ------------------------------------------------------------------
  useEffect(() => {
    const fetchTeams = async () => {
      if (!selectedProgramId) return;

      if (!urlTeamId && !queryTeamId) setSelectedTeamId("");
      setTeams([]);

      const { data, error } = await supabase
        .from("teams")
        .select("id, name")
        .eq("program_id", selectedProgramId)
        .order("name", { ascending: true });

      if (!error) {
        const nextTeams = data || [];
        setTeams(nextTeams);
        setSelectedTeamId((prev) => {
          const preferredTeamId = urlTeamId || queryTeamId || prev || "";
          if (!preferredTeamId) return "";
          const existsInProgram = nextTeams.some((team) => String(team.id) === String(preferredTeamId));
          return existsInProgram ? preferredTeamId : "";
        });
      }
    };
    fetchTeams();
  }, [selectedProgramId, urlTeamId, queryTeamId, refreshKey]);

  // ------------------------------------------------------------------
  // 4. HANDLERS & LOADING
  // ------------------------------------------------------------------
  const handleEditTeam = () => {
    if (!selectedProgramId || !selectedTeamId) return;
    navigate(`/admin/program/${selectedProgramId}/team/${selectedTeamId}/edit`);
  };

  const handleDownloadRosterTemplate = () => {
    const headers = ["First", "Last", "Email", "Jersey Number", "Position"];
    const sampleRow = ["Jordan", "Lee", "jordan.lee@example.com", "12", "Midfielder"];
    const csv = [headers.join(","), sampleRow.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "roster-template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleRosterCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!selectedProgramId || !selectedTeamId) {
      setCsvImportError("Select a program and team before importing a roster CSV.");
      return;
    }

    setCsvImportBusy(true);
    setCsvImportError("");
    setCsvImportMessage("");

    try {
      const text = await file.text();
      const rows = parseCsvRows(text);
      if (rows.length === 0) {
        throw new Error("The CSV file was empty.");
      }

      const existingRosterRes = await supabase
        .from("rostered_users")
        .select("id, user_id, teamsnap_email, teamsnap_name")
        .eq("team_id", selectedTeamId);
      if (existingRosterRes.error) throw existingRosterRes.error;

      const existingByUserId = new Map();
      const existingByEmail = new Map();
      const existingByName = new Map();
      (existingRosterRes.data || []).forEach((row) => {
        if (row.user_id) existingByUserId.set(String(row.user_id), row);
        if (row.teamsnap_email) existingByEmail.set(normalizeEmail(row.teamsnap_email), row);
        if (row.teamsnap_name) existingByName.set(String(row.teamsnap_name).trim().toLowerCase(), row);
      });

      let importedCount = 0;
      let updatedCount = 0;

      for (const row of rows) {
        const firstName = String(row.First || "").trim();
        const lastName = String(row.Last || "").trim();
        const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
        const firstEmail = String(row.Email || "")
          .split(";")
          .map((value) => normalizeEmail(value))
          .find(Boolean) || "";

        if (!fullName && !firstEmail) continue;

        let matchedUserId = null;
        if (firstEmail) {
          const { data: matchedUser } = await supabase
            .from("users")
            .select("id, email")
            .ilike("email", firstEmail)
            .maybeSingle();
          matchedUserId = matchedUser?.id || null;
        }

        const existingRow = (
          (matchedUserId ? existingByUserId.get(String(matchedUserId)) : null) ||
          (firstEmail ? existingByEmail.get(firstEmail) : null) ||
          (fullName ? existingByName.get(fullName.toLowerCase()) : null) ||
          null
        );

        const payload = {
          team_id: selectedTeamId,
          user_id: existingRow?.user_id || matchedUserId || null,
          role: "Athlete",
          number: row["Jersey Number"] ? Number(row["Jersey Number"]) || null : null,
          position: String(row.Position || "").trim() || null,
          teamsnap_name: fullName || null,
          teamsnap_email: firstEmail || null,
        };

        if (existingRow) {
          const { error } = await supabase
            .from("rostered_users")
            .update(payload)
            .eq("id", existingRow.id);
          if (error) throw error;
          updatedCount += 1;
          if (payload.user_id) existingByUserId.set(String(payload.user_id), { ...existingRow, ...payload });
          if (payload.teamsnap_email) existingByEmail.set(payload.teamsnap_email, { ...existingRow, ...payload });
          if (payload.teamsnap_name) existingByName.set(payload.teamsnap_name.toLowerCase(), { ...existingRow, ...payload });
        } else {
          const { data, error } = await supabase
            .from("rostered_users")
            .insert(payload)
            .select("id, user_id, teamsnap_email, teamsnap_name")
            .single();
          if (error) throw error;
          importedCount += 1;
          if (data?.user_id) existingByUserId.set(String(data.user_id), data);
          if (data?.teamsnap_email) existingByEmail.set(normalizeEmail(data.teamsnap_email), data);
          if (data?.teamsnap_name) existingByName.set(String(data.teamsnap_name).toLowerCase(), data);
        }
      }

      setCsvImportMessage(`Roster import complete. ${importedCount} added, ${updatedCount} updated.`);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Roster CSV import error:", error);
      setCsvImportError(error.message || "Unable to import roster CSV.");
    } finally {
      setCsvImportBusy(false);
    }
  };

  const handleRemoveAthlete = async (athlete) => {
    if (!athlete) return;

    const athleteName = String(
      athlete.users?.full_name ||
      athlete.teamsnap_name ||
      athlete.email ||
      "this athlete"
    ).trim();
    const confirmed = window.confirm(`Remove ${athleteName} from this roster?`);
    if (!confirmed) return;

    setOpenActionMenu(null);
    setLoading(true);
    try {
      if (athlete.type === "pending") {
        const { error: deleteInviteError } = await supabase
          .from("player_invites")
          .delete()
          .eq("id", athlete.user_id);
        if (deleteInviteError) throw deleteInviteError;
        setAthletes((prev) => prev.filter((row) => String(row.user_id) !== String(athlete.user_id)));
        return;
      }

      const rosterRowId = String(athlete.id || "").trim();
      if (!rosterRowId) {
        throw new Error("Missing roster row id.");
      }

      const athleteEmail = normalizeEmail(athlete.teamsnap_email || athlete.users?.email || athlete.email);

      const { error: deleteRosterError } = await supabase
        .from("rostered_users")
        .delete()
        .eq("id", rosterRowId);
      if (deleteRosterError) throw deleteRosterError;

      if (selectedProgramId && athlete.team_id && athleteEmail) {
        const { error: deleteInviteError } = await supabase
          .from("player_invites")
          .delete()
          .eq("program_id", selectedProgramId)
          .eq("team_id", athlete.team_id)
          .ilike("email", athleteEmail)
          .eq("status", "pending");
        if (deleteInviteError) throw deleteInviteError;
      }

      setAthletes((prev) => prev.filter((row) => String(row.id || "") !== rosterRowId));
    } catch (error) {
      console.error("Error removing athlete:", error);
      alert("Unable to remove athlete: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleOpenEditRosterContact = (athlete) => {
    if (!athlete || athlete.type === "pending") return;
    setOpenActionMenu(null);
    setEditingRosterContact(athlete);
    setRosterContactForm({
      name: String(athlete.teamsnap_name || athlete.users?.full_name || "").trim(),
      email: String(athlete.teamsnap_email || athlete.users?.email || athlete.email || "").trim()
    });
    setRosterContactErrors({ name: "", email: "" });
  };

  const handleSaveRosterContact = async () => {
    if (!editingRosterContact?.id) return;
    const name = String(rosterContactForm.name || "").trim();
    const email = normalizeEmail(rosterContactForm.email);
    const errors = {
      name: name.length >= 2 ? "" : "Name must be at least 2 characters.",
      email: EMAIL_REGEX.test(email) ? "" : "Enter a valid email address."
    };
    setRosterContactErrors(errors);
    if (errors.name || errors.email) return;

    setLoading(true);
    try {
      const { data: matchedUser, error: matchedUserError } = await supabase
        .from("users")
        .select("id, email")
        .ilike("email", email)
        .maybeSingle();
      if (matchedUserError) throw matchedUserError;

      const { error: updateError } = await supabase
        .from("rostered_users")
        .update({
          teamsnap_name: name,
          teamsnap_email: email,
          user_id: matchedUser?.id || null,
          updated_at: new Date().toISOString()
        })
        .eq("id", editingRosterContact.id);
      if (updateError) throw updateError;

      setAthletes((prev) => prev.map((row) => {
        if (String(row.id || "") !== String(editingRosterContact.id || "")) return row;
        return {
          ...row,
          user_id: matchedUser?.id || null,
          teamsnap_name: name,
          teamsnap_email: email,
          email,
          users: {
            ...(row.users || {}),
            full_name: name,
            email
          }
        };
      }));

      setEditingRosterContact(null);
      setRosterContactForm({ name: "", email: "" });
      setRosterContactErrors({ name: "", email: "" });
    } catch (error) {
      console.error("Error updating roster contact:", error);
      alert("Unable to update contact info: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleRemoveCoach = async (coach) => { /* Logic Placeholder */ };

  const buildSwotPayloadFromPlayerType = async (playerTypeId) => {
    const { data: swotRows, error: swotRowsError } = await supabase
      .from("player_type_swot")
      .select("skill_category_id, skill_focus_id, swot_type, priority, notes")
      .eq("player_type_id", playerTypeId);
    if (swotRowsError) throw swotRowsError;
    if (!swotRows || swotRows.length === 0) return null;

    const sectionIds = Array.from(
      new Set(swotRows.map((row) => row.skill_category_id).filter(Boolean))
    );
    const itemIds = Array.from(
      new Set(swotRows.map((row) => row.skill_focus_id).filter(Boolean))
    );

    const [sectionsRes, itemsRes] = await Promise.all([
      sectionIds.length
        ? supabase
            .from("development_sections")
            .select("id, name")
            .in("id", sectionIds)
        : Promise.resolve({ data: [], error: null }),
      itemIds.length
        ? supabase
            .from("development_items")
            .select("id, name")
            .in("id", itemIds)
        : Promise.resolve({ data: [], error: null })
    ]);

    if (sectionsRes.error) throw sectionsRes.error;
    if (itemsRes.error) throw itemsRes.error;

    const sectionById = new Map((sectionsRes.data || []).map((row) => [row.id, row.name]));
    const itemById = new Map((itemsRes.data || []).map((row) => [row.id, row.name]));
    const buckets = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
    const swotTypeMap = {
      Strength: "strengths",
      Weakness: "weaknesses",
      Opportunity: "opportunities",
      Threat: "threats"
    };

    (swotRows || []).forEach((row) => {
      const bucketKey = swotTypeMap[row.swot_type];
      if (!bucketKey) return;
      const sectionName = sectionById.get(row.skill_category_id) || "General";
      const itemName = itemById.get(row.skill_focus_id) || "Skill Focus";
      buckets[bucketKey].push({
        id: uuidv4(),
        section: sectionName,
        sectionId: row.skill_category_id || null,
        developmentItemId: row.skill_focus_id || null,
        item: itemName,
        completed: false,
        priority: false,
        priorityRank: null,
        actions: [],
        notes: row.notes || ""
      });
    });

    return buckets;
  };

  const handleAssignPlayerType = async (athlete, playerTypeId) => {
    if (!athlete || athlete.type === "pending") return;
    const athleteKey = getAthletePlayerTypeKey(athlete);
    if (!athleteKey) return;

    if (athlete.user_id && !programSportId) {
      alert("Program sport could not be matched. Please set the program sport.");
      return;
    }

    setPlayerTypeSaving((prev) => ({ ...prev, [athleteKey]: true }));
    try {
      const nextPlayerTypeId = playerTypeId || null;
      const athleteEmail = String(athlete.users?.email || athlete.teamsnap_email || athlete.email || "").trim();
      if (!athlete.user_id) {
        setPlayerTypeDraftByAthlete((prev) => ({
          ...prev,
          [athleteKey]: nextPlayerTypeId || ""
        }));

        if (nextPlayerTypeId && selectedProgramId) {
          const swotPayload = await buildSwotPayloadFromPlayerType(nextPlayerTypeId);
          const draftEmail = String(athlete.users?.email || athlete.teamsnap_email || athlete.email || "").trim();
          const draftName = String(athlete.users?.full_name || athlete.teamsnap_name || "Imported Athlete").trim();

          if (swotPayload) {
            let primaryDraft = null;
            let fallbackDraft = null;
            if (draftEmail) {
              const { data: primaryRows, error: primaryError } = await supabase
                .from("athlete_swot")
                .select("id, status, position_id")
                .eq("program_id", selectedProgramId)
                .is("athlete_id", null)
                .ilike("prebuilt_email", draftEmail)
                .not("position_id", "is", null)
                .is("secondary_position_id", null)
                .order("updated_at", { ascending: false })
                .limit(1);
              if (primaryError) throw primaryError;
              primaryDraft = (primaryRows || [])[0] || null;

              const { data: fallbackRows, error: fallbackError } = await supabase
                .from("athlete_swot")
                .select("id, status")
                .eq("program_id", selectedProgramId)
                .is("athlete_id", null)
                .is("position_id", null)
                .is("secondary_position_id", null)
                .ilike("prebuilt_email", draftEmail)
                .order("updated_at", { ascending: false })
                .limit(1);
              if (fallbackError) throw fallbackError;
              fallbackDraft = (fallbackRows || [])[0] || null;
            }

            const swotRowPayload = {
              status: primaryDraft?.status || fallbackDraft?.status || "draft",
              strengths: swotPayload.strengths,
              weaknesses: swotPayload.weaknesses,
              opportunities: swotPayload.opportunities,
              threats: swotPayload.threats,
              player_type: nextPlayerTypeId,
              prebuilt_name: draftName || null,
              prebuilt_email: draftEmail || null,
              updated_at: new Date().toISOString()
            };

            if (primaryDraft?.id) {
              const { data: updatedDraft, error: updateDraftError } = await supabase
                .from("athlete_swot")
                .update(swotRowPayload)
                .eq("id", primaryDraft.id)
                .is("athlete_id", null)
                .select("id, prebuilt_name, prebuilt_email, status, created_at, strengths, weaknesses, opportunities, threats")
                .single();
              if (updateDraftError) throw updateDraftError;
              upsertDraftEvaluation(updatedDraft);
            } else if (fallbackDraft?.id) {
              const { data: updatedDraft, error: updateDraftError } = await supabase
                .from("athlete_swot")
                .update(swotRowPayload)
                .eq("id", fallbackDraft.id)
                .is("athlete_id", null)
                .select("id, prebuilt_name, prebuilt_email, status, created_at, strengths, weaknesses, opportunities, threats")
                .single();
              if (updateDraftError) throw updateDraftError;
              upsertDraftEvaluation(updatedDraft);
            } else {
              const { data: insertedDraft, error: insertDraftError } = await supabase
                .from("athlete_swot")
                .insert([{
                  program_id: selectedProgramId,
                  athlete_id: null,
                  position_id: null,
                  secondary_position_id: null,
                  ...swotRowPayload
                }])
                .select("id, prebuilt_name, prebuilt_email, status, created_at, strengths, weaknesses, opportunities, threats")
                .single();
              if (insertDraftError) throw insertDraftError;
              upsertDraftEvaluation(insertedDraft);
            }
          }
        }
      } else {
        const existingInfo = playerTypeByAthlete[athlete.user_id];

        if (existingInfo?.id) {
          const { error } = await supabase
            .from("user_sport_info")
            .update({ player_type: nextPlayerTypeId })
            .eq("id", existingInfo.id);
          if (error) throw error;
        } else if (nextPlayerTypeId) {
          const { data, error } = await supabase
            .from("user_sport_info")
            .insert([{
              user_id: athlete.user_id,
              sport: programSportId,
              player_type: nextPlayerTypeId
            }])
            .select("id, user_id, player_type")
            .maybeSingle();
          if (error) throw error;
          if (data) {
            setPlayerTypeByAthlete((prev) => ({ ...prev, [athlete.user_id]: data }));
          }
        }

        if (existingInfo?.id) {
          setPlayerTypeByAthlete((prev) => ({
            ...prev,
            [athlete.user_id]: { ...existingInfo, player_type: nextPlayerTypeId }
          }));
        }
      }

      syncDraftPlayerTypeByEmail(athleteEmail, nextPlayerTypeId);
      if (athlete.user_id) {
        setPlayerTypeByAthlete((prev) => ({
          ...prev,
          [athleteKey]: { ...(prev[athleteKey] || {}), player_type: nextPlayerTypeId }
        }));
      }

      if (athlete.user_id && nextPlayerTypeId && selectedProgramId) {
        const positionIds = athletePositionIdsByAthlete?.[athleteKey] || [];
        const primaryCandidate = String(positionIds[0] || "").trim();
        const secondaryMap = secondaryPlayerTypeByAthletePosition?.[athleteKey] || {};
        const primaryPositionId = (
          primaryCandidate &&
          positionIds.length === 1 &&
          Object.prototype.hasOwnProperty.call(secondaryMap, String(primaryCandidate))
        )
          ? ""
          : primaryCandidate;

        const { data: existingPrimaryRows, error: existingPrimaryError } = await supabase
          .from("athlete_swot")
          .select("id, position_id, status")
          .eq("program_id", selectedProgramId)
          .eq("athlete_id", athlete.user_id)
          .not("position_id", "is", null)
          .is("secondary_position_id", null)
          .order("updated_at", { ascending: false })
          .limit(1);
        if (existingPrimaryError) throw existingPrimaryError;
        const existingPrimary = (existingPrimaryRows || [])[0] || null;
        const resolvedPrimaryPositionId = primaryPositionId || String(existingPrimary?.position_id || "").trim();
        if (!resolvedPrimaryPositionId) {
          return;
        }
        console.log("[AdminDashboard] Assign player type (primary)", {
          athleteId: athlete.user_id,
          programId: selectedProgramId,
          primaryPositionId,
          resolvedPrimaryPositionId,
          existingPrimaryId: existingPrimary?.id || null,
          existingPrimaryPositionId: existingPrimary?.position_id || null,
          nextPlayerTypeId
        });

        const swotPayload = await buildSwotPayloadFromPlayerType(nextPlayerTypeId);
        if (swotPayload) {
          const { data: primaryRows, error: primaryError } = await supabase
            .from("athlete_swot")
            .select("id, status, position_id")
            .eq("program_id", selectedProgramId)
            .eq("athlete_id", athlete.user_id)
            .not("position_id", "is", null)
            .is("secondary_position_id", null)
            .order("updated_at", { ascending: false })
            .limit(1);
          if (primaryError) throw primaryError;
          let primaryRow = (primaryRows || [])[0] || null;
          const athleteEmail = normalizeEmail(athlete.users?.email || athlete.teamsnap_email || athlete.email);
          if (!primaryRow?.id && athleteEmail) {
            const { data: draftPrimaryRows, error: draftPrimaryError } = await supabase
              .from("athlete_swot")
              .select("id, status, position_id")
              .eq("program_id", selectedProgramId)
              .is("athlete_id", null)
              .ilike("prebuilt_email", athleteEmail)
              .eq("position_id", resolvedPrimaryPositionId)
              .is("secondary_position_id", null)
              .order("updated_at", { ascending: false })
              .limit(1);
            if (draftPrimaryError) throw draftPrimaryError;
            primaryRow = (draftPrimaryRows || [])[0] || null;
          }
          console.log("[AdminDashboard] Primary row lookup", {
            athleteId: athlete.user_id,
            programId: selectedProgramId,
            resolvedPrimaryPositionId,
            primaryRowId: primaryRow?.id || null,
            primaryRowPositionId: primaryRow?.position_id || null
          });
          const updatePayload = {
            strengths: swotPayload.strengths,
            weaknesses: swotPayload.weaknesses,
            opportunities: swotPayload.opportunities,
            threats: swotPayload.threats,
            player_type: nextPlayerTypeId,
            updated_at: new Date().toISOString()
          };

          if (primaryRow?.id) {
            console.log("[AdminDashboard] Updating primary row", {
              primaryRowId: primaryRow.id,
              resolvedPrimaryPositionId,
              nextPlayerTypeId
            });
            const { error: updateError } = await supabase
              .from("athlete_swot")
              .update({
                ...updatePayload,
                athlete_id: athlete.user_id,
                prebuilt_name: null,
                prebuilt_email: null
              })
              .eq("id", primaryRow.id);
            if (updateError) throw updateError;
          } else if (resolvedPrimaryPositionId) {
            console.log("[AdminDashboard] Inserting primary row", {
              athleteId: athlete.user_id,
              programId: selectedProgramId,
              resolvedPrimaryPositionId,
              nextPlayerTypeId
            });
            const { error: insertError } = await supabase
              .from("athlete_swot")
              .insert([{
                program_id: selectedProgramId,
                athlete_id: athlete.user_id,
                status: "draft",
                position_id: resolvedPrimaryPositionId,
                secondary_position_id: null,
                ...updatePayload
              }]);
            if (insertError) throw insertError;
          }
        }
      }
    } catch (err) {
      console.error("Error assigning player type:", err);
      alert("Unable to update player type: " + err.message);
    } finally {
      setPlayerTypeSaving((prev) => ({ ...prev, [athleteKey]: false }));
    }
  };

  const handlePlayerTypeDraftChange = (athleteOrId, value) => {
    const athleteId = typeof athleteOrId === "string" ? athleteOrId : getAthletePlayerTypeKey(athleteOrId);
    if (!athleteId) return;
    setPlayerTypeDraftByAthlete((prev) => ({
      ...prev,
      [athleteId]: value
    }));
  };

  const handleSaveAthletePositions = async (athlete, primaryPositionId, secondaryPositionId) => {
    if (!selectedProgramId) {
      alert("Please select a program before saving positions.");
      return;
    }
    const athleteKey = getAthletePlayerTypeKey(athlete);
    if (!athleteKey) return;

    setPositionSaving((prev) => ({ ...prev, [athleteKey]: true }));
    try {
      const normalizedPrimary = primaryPositionId || null;
      const normalizedSecondary = secondaryPositionId || null;
      const draftEmail = normalizeEmail(athlete.users?.email || athlete.teamsnap_email || athlete.email);
      const draftName = String(athlete.users?.full_name || athlete.teamsnap_name || "Imported Athlete").trim();

      if (athlete.user_id) {
        const safeSecondary = normalizedSecondary === normalizedPrimary ? null : normalizedSecondary;
        if (normalizedPrimary) {
          const { data: existingRows, error } = await supabase
            .from("athlete_swot")
            .select("id, player_type")
            .eq("program_id", selectedProgramId)
            .eq("athlete_id", athlete.user_id)
            .eq("position_id", normalizedPrimary)
            .order("updated_at", { ascending: false })
            .limit(1);
          if (error) throw error;
          const existing = (existingRows || [])[0];

          const { data: fallbackRows, error: fallbackError } = await supabase
            .from("athlete_swot")
            .select("id, player_type")
            .eq("program_id", selectedProgramId)
            .eq("athlete_id", athlete.user_id)
            .is("position_id", null)
            .is("secondary_position_id", null)
            .order("updated_at", { ascending: false })
            .limit(1);
          if (fallbackError) throw fallbackError;
          const fallback = (fallbackRows || [])[0];

          if (existing?.id) {
            const updatePayload = {
              updated_at: new Date().toISOString()
            };
            if (!existing.player_type && fallback?.player_type) {
              updatePayload.player_type = fallback.player_type;
            }
            const { error: updateError } = await supabase
              .from("athlete_swot")
              .update(updatePayload)
              .eq("id", existing.id);
            if (updateError) throw updateError;

            if (fallback?.id && fallback.id !== existing.id) {
              const { error: deleteError } = await supabase
                .from("athlete_swot")
                .delete()
                .eq("id", fallback.id);
              if (deleteError) throw deleteError;
            }
          } else if (fallback?.id) {
            const { error: updateError } = await supabase
              .from("athlete_swot")
              .update({
                position_id: normalizedPrimary,
                secondary_position_id: null,
                updated_at: new Date().toISOString()
              })
              .eq("id", fallback.id);
            if (updateError) throw updateError;
          } else {
            const { error: insertError } = await supabase
              .from("athlete_swot")
              .insert([{
                program_id: selectedProgramId,
                athlete_id: athlete.user_id,
                status: "draft",
                position_id: normalizedPrimary,
                secondary_position_id: null
              }]);
            if (insertError) throw insertError;
          }
        }

        if (safeSecondary) {
          const { data: secondaryRows, error: secondaryError } = await supabase
            .from("athlete_swot")
            .select("id")
            .eq("program_id", selectedProgramId)
            .eq("athlete_id", athlete.user_id)
            .eq("secondary_position_id", safeSecondary)
            .order("updated_at", { ascending: false })
            .limit(1);
          if (secondaryError) throw secondaryError;
          const secondaryExisting = (secondaryRows || [])[0];

          if (secondaryExisting?.id) {
            const { error: updateError } = await supabase
              .from("athlete_swot")
              .update({
                updated_at: new Date().toISOString()
              })
              .eq("id", secondaryExisting.id);
            if (updateError) throw updateError;
          } else {
            const { error: insertError } = await supabase
              .from("athlete_swot")
              .insert([{
                program_id: selectedProgramId,
                athlete_id: athlete.user_id,
                status: "draft",
                position_id: null,
                secondary_position_id: safeSecondary
              }]);
            if (insertError) throw insertError;
          }
        } else {
          const { error: clearError } = await supabase
            .from("athlete_swot")
            .delete()
            .eq("program_id", selectedProgramId)
            .eq("athlete_id", athlete.user_id)
            .is("position_id", null)
            .not("secondary_position_id", "is", null);
          if (clearError) throw clearError;
        }
        return;
      }

      if (!draftEmail) return;
      const safeSecondaryDraft = normalizedSecondary === normalizedPrimary ? null : normalizedSecondary;

      if (normalizedPrimary) {
        const { data: primaryDraftRows, error: primaryDraftError } = await supabase
          .from("athlete_swot")
          .select("id, player_type")
          .eq("program_id", selectedProgramId)
          .is("athlete_id", null)
          .ilike("prebuilt_email", draftEmail)
          .eq("position_id", normalizedPrimary)
          .order("updated_at", { ascending: false })
          .limit(1);
        if (primaryDraftError) throw primaryDraftError;
        const primaryDraft = (primaryDraftRows || [])[0];

        const { data: fallbackDraftRows, error: fallbackDraftError } = await supabase
          .from("athlete_swot")
          .select("id, player_type")
          .eq("program_id", selectedProgramId)
          .is("athlete_id", null)
          .ilike("prebuilt_email", draftEmail)
          .is("position_id", null)
          .is("secondary_position_id", null)
          .order("updated_at", { ascending: false })
          .limit(1);
        if (fallbackDraftError) throw fallbackDraftError;
        const fallbackDraft = (fallbackDraftRows || [])[0];

        if (primaryDraft?.id) {
          const updatePayload = {
            updated_at: new Date().toISOString()
          };
          if (!primaryDraft.player_type && fallbackDraft?.player_type) {
            updatePayload.player_type = fallbackDraft.player_type;
          }
          const { error: updateError } = await supabase
            .from("athlete_swot")
            .update(updatePayload)
            .eq("id", primaryDraft.id)
            .is("athlete_id", null);
          if (updateError) throw updateError;

          if (fallbackDraft?.id && fallbackDraft.id !== primaryDraft.id) {
            const { error: deleteError } = await supabase
              .from("athlete_swot")
              .delete()
              .eq("id", fallbackDraft.id);
            if (deleteError) throw deleteError;
          }
        } else if (fallbackDraft?.id) {
          const { error: updateError } = await supabase
            .from("athlete_swot")
            .update({
              position_id: normalizedPrimary,
              secondary_position_id: null,
              prebuilt_name: draftName || null,
              prebuilt_email: draftEmail || null,
              updated_at: new Date().toISOString()
            })
            .eq("id", fallbackDraft.id)
            .is("athlete_id", null);
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from("athlete_swot")
            .insert([{
              program_id: selectedProgramId,
              athlete_id: null,
              status: "draft",
              prebuilt_name: draftName || null,
              prebuilt_email: draftEmail || null,
              position_id: normalizedPrimary,
              secondary_position_id: null
            }]);
          if (insertError) {
            if (insertError.code === "23505") {
              const { error: updateError } = await supabase
                .from("athlete_swot")
                .update({
                  updated_at: new Date().toISOString()
                })
                .eq("program_id", selectedProgramId)
                .ilike("prebuilt_email", draftEmail)
                .eq("position_id", normalizedPrimary)
                .eq("status", "draft");
              if (updateError) throw updateError;
            } else {
              throw insertError;
            }
          }
        }
      }

      if (safeSecondaryDraft) {
        const { data: secondaryDraftRows, error: secondaryDraftError } = await supabase
          .from("athlete_swot")
          .select("id")
          .eq("program_id", selectedProgramId)
          .is("athlete_id", null)
          .ilike("prebuilt_email", draftEmail)
          .eq("secondary_position_id", safeSecondaryDraft)
          .order("updated_at", { ascending: false })
          .limit(1);
        if (secondaryDraftError) throw secondaryDraftError;
        const secondaryDraft = (secondaryDraftRows || [])[0];

        if (secondaryDraft?.id) {
          const { error: updateError } = await supabase
            .from("athlete_swot")
            .update({
              updated_at: new Date().toISOString()
            })
            .eq("id", secondaryDraft.id)
            .is("athlete_id", null);
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from("athlete_swot")
            .insert([{
              program_id: selectedProgramId,
              athlete_id: null,
              status: "draft",
              prebuilt_name: draftName || null,
              prebuilt_email: draftEmail || null,
              position_id: null,
              secondary_position_id: safeSecondaryDraft
            }]);
          if (insertError) {
            if (insertError.code === "23505") {
              const { error: updateError } = await supabase
                .from("athlete_swot")
                .update({
                  updated_at: new Date().toISOString()
                })
                .eq("program_id", selectedProgramId)
                .ilike("prebuilt_email", draftEmail)
                .eq("secondary_position_id", safeSecondaryDraft)
                .eq("status", "draft");
              if (updateError) throw updateError;
            } else {
              throw insertError;
            }
          }
        }
      } else {
        const { error: clearError } = await supabase
          .from("athlete_swot")
          .delete()
          .eq("program_id", selectedProgramId)
          .is("athlete_id", null)
          .ilike("prebuilt_email", draftEmail)
          .is("position_id", null)
          .not("secondary_position_id", "is", null);
        if (clearError) throw clearError;
      }
      return;
    }
    catch (err) {
      console.error("Error saving positions:", err);
      alert("Unable to update positions: " + err.message);
    } finally {
      setPositionSaving((prev) => ({ ...prev, [athleteKey]: false }));
    }
  };

  const handlePublishAthleteSwot = async (athlete) => {
    if (!selectedProgramId || !athlete?.user_id) return;
    try {
      const { data: swotRows, error } = await supabase
        .from("athlete_swot")
        .select("id")
        .eq("program_id", selectedProgramId)
        .eq("athlete_id", athlete.user_id)
        .order("updated_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      const latest = (swotRows || [])[0];
      if (!latest?.id) return;

      const { error: updateError } = await supabase
        .from("athlete_swot")
        .update({ status: "published", updated_at: new Date().toISOString() })
        .eq("id", latest.id);
      if (updateError) throw updateError;
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error("Error publishing athlete SWOT:", err);
      alert("Unable to publish SWOT: " + err.message);
    }
  };

  const handlePositionsSavedRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const syncDraftPlayerTypeByEmail = (email, playerTypeId) => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return;
    setPlayerTypeDraftByAthlete((prev) => {
      const next = { ...prev };
      draftEvaluations.forEach((draft) => {
        if (normalizeEmail(draft.prebuilt_email) !== normalizedEmail) return;
        const draftKey = getAthletePlayerTypeKey(draft);
        if (!draftKey) return;
        next[draftKey] = playerTypeId || "";
      });
      return next;
    });
  };

  const upsertDraftEvaluation = (draftRow) => {
    if (!draftRow?.id && !draftRow?.prebuilt_email) return;
    setDraftEvaluations((prev) => {
      const next = prev.filter((row) =>
        String(row?.id || "") !== String(draftRow?.id || "") &&
        normalizeEmail(row?.prebuilt_email) !== normalizeEmail(draftRow?.prebuilt_email)
      );
      return [draftRow, ...next];
    });
  };

  const handleSavePlayerType = async (athlete) => {
    const drafted = playerTypeDraftByAthlete[getAthletePlayerTypeKey(athlete)] || "";
    await handleAssignPlayerType(athlete, drafted);
  };

  const handleSavePlayerTypeWithValue = async (athlete, playerTypeId) => {
    await handleAssignPlayerType(athlete, playerTypeId || "");
  };

  const handleSaveSecondaryPlayerType = async (athlete, secondaryPositionId, playerTypeId) => {
    if (!selectedProgramId || !secondaryPositionId) return;
    const normalizedPlayerTypeId = playerTypeId || null;
    const draftEmail = normalizeEmail(athlete.users?.email || athlete.teamsnap_email || athlete.email);

    try {
      const swotPayload = normalizedPlayerTypeId
        ? await buildSwotPayloadFromPlayerType(normalizedPlayerTypeId)
        : null;
      const swotFields = swotPayload
        ? {
          strengths: swotPayload.strengths,
          weaknesses: swotPayload.weaknesses,
          opportunities: swotPayload.opportunities,
          threats: swotPayload.threats
        }
        : {};

      if (athlete.user_id) {
        const { data: existingRows, error } = await supabase
          .from("athlete_swot")
          .select("id")
          .eq("program_id", selectedProgramId)
          .eq("athlete_id", athlete.user_id)
          .eq("secondary_position_id", secondaryPositionId)
          .order("updated_at", { ascending: false })
          .limit(1);
        if (error) throw error;
        const existing = (existingRows || [])[0];

        if (existing?.id) {
          const { error: updateError } = await supabase
            .from("athlete_swot")
            .update({
              player_type: normalizedPlayerTypeId,
              ...swotFields,
              updated_at: new Date().toISOString()
            })
            .eq("id", existing.id);
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from("athlete_swot")
            .insert([{
              program_id: selectedProgramId,
              athlete_id: athlete.user_id,
              status: "draft",
              position_id: null,
              secondary_position_id: secondaryPositionId,
              player_type: normalizedPlayerTypeId,
              ...swotFields
            }]);
          if (insertError) throw insertError;
        }
        return;
      }

      if (!draftEmail) return;
      const { data: draftRows, error: draftError } = await supabase
        .from("athlete_swot")
        .select("id")
        .eq("program_id", selectedProgramId)
        .is("athlete_id", null)
        .ilike("prebuilt_email", draftEmail)
        .eq("secondary_position_id", secondaryPositionId)
        .order("updated_at", { ascending: false })
        .limit(1);
      if (draftError) throw draftError;
      const existingDraft = (draftRows || [])[0];

      if (existingDraft?.id) {
        const { error: updateError } = await supabase
          .from("athlete_swot")
          .update({
            player_type: normalizedPlayerTypeId,
            ...swotFields,
            updated_at: new Date().toISOString()
          })
          .eq("id", existingDraft.id)
          .is("athlete_id", null);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from("athlete_swot")
          .insert([{
            program_id: selectedProgramId,
            athlete_id: null,
            status: "draft",
            prebuilt_email: draftEmail || null,
            prebuilt_name: athlete.users?.full_name || athlete.teamsnap_name || null,
            position_id: null,
            secondary_position_id: secondaryPositionId,
            player_type: normalizedPlayerTypeId,
            ...swotFields
          }]);
        if (insertError) {
          if (String(insertError.code || "") === "23505") {
            const { error: updateError } = await supabase
              .from("athlete_swot")
              .update({
                player_type: normalizedPlayerTypeId,
                ...swotFields,
                updated_at: new Date().toISOString()
              })
              .eq("program_id", selectedProgramId)
              .is("athlete_id", null)
              .ilike("prebuilt_email", draftEmail)
              .is("position_id", null)
              .eq("secondary_position_id", secondaryPositionId);
            if (updateError) throw updateError;
          } else {
            throw insertError;
          }
        }
      }
    } catch (err) {
      console.error("Error saving secondary player type:", err);
      alert("Unable to update secondary player type: " + err.message);
    }
  };

  const ensureDraftSwotForAthlete = async (athlete) => {
    if (!selectedProgramId) return null;
    const draftEmail = String(athlete?.users?.email || athlete?.teamsnap_email || athlete?.email || "").trim();
    const draftName = String(athlete?.users?.full_name || athlete?.teamsnap_name || "Imported Athlete").trim();
    if (!draftEmail) return null;

    const { data: existingDraftRows, error: existingDraftError } = await supabase
      .from("athlete_swot")
      .select("id, prebuilt_name, prebuilt_email, status, created_at")
      .eq("program_id", selectedProgramId)
      .is("athlete_id", null)
      .is("position_id", null)
      .ilike("prebuilt_email", draftEmail)
      .order("created_at", { ascending: false });
    if (existingDraftError) throw existingDraftError;

    const existingDraft = (existingDraftRows || [])[0] || null;
    if (existingDraft?.id) return existingDraft;

    const { data: insertedDraft, error: insertDraftError } = await supabase
      .from("athlete_swot")
      .insert([{
        program_id: selectedProgramId,
        athlete_id: null,
        position_id: null,
        prebuilt_name: draftName || null,
        prebuilt_email: draftEmail,
        status: "draft"
      }])
      .select("id, prebuilt_name, prebuilt_email, status, created_at")
      .single();

    if (insertDraftError) {
      const isDuplicateDraft =
        String(insertDraftError.code || "") === "23505" ||
        String(insertDraftError.message || "").includes("idx_athlete_swot_program_prebuilt_email_position_draft_unique");

      if (!isDuplicateDraft) throw insertDraftError;

      const { data: fallbackDraftRows, error: fallbackDraftError } = await supabase
        .from("athlete_swot")
        .select("id, prebuilt_name, prebuilt_email, status, created_at")
        .eq("program_id", selectedProgramId)
        .is("athlete_id", null)
        .is("position_id", null)
        .ilike("prebuilt_email", draftEmail)
        .order("created_at", { ascending: false });
      if (fallbackDraftError) throw fallbackDraftError;

      const fallbackDraft = (fallbackDraftRows || [])[0] || null;
      if (fallbackDraft?.id) return fallbackDraft;
      throw insertDraftError;
    }

    setDraftEvaluations((prev) => [insertedDraft, ...prev]);
    return insertedDraft;
  };

  const handleAssignDraftPlayerType = async (draft, playerTypeId) => {
    if (!draft?.id || !selectedProgramId) return;
    const athleteKey = getAthletePlayerTypeKey(draft);
    const nextPlayerTypeId = playerTypeId || null;
    if (!nextPlayerTypeId) return;

    setPlayerTypeSaving((prev) => ({ ...prev, [athleteKey]: true }));
    try {
      const swotPayload = await buildSwotPayloadFromPlayerType(nextPlayerTypeId);
      if (!swotPayload) {
        throw new Error("No SWOT archetype found for that player type.");
      }

      const draftEmail = String(draft.prebuilt_email || "").trim();
      const draftName = String(draft.prebuilt_name || "Draft Athlete").trim();
      if (!draftEmail) {
        throw new Error("Draft athlete is missing an email address.");
      }

      const { data: primaryDraftRows, error: primaryDraftError } = await supabase
        .from("athlete_swot")
        .select("id, status, position_id")
        .eq("program_id", selectedProgramId)
        .is("athlete_id", null)
        .ilike("prebuilt_email", draftEmail)
        .not("position_id", "is", null)
        .is("secondary_position_id", null)
        .order("updated_at", { ascending: false })
        .limit(1);
      if (primaryDraftError) throw primaryDraftError;
      const primaryDraft = (primaryDraftRows || [])[0] || null;

      console.log("[AdminDashboard] Draft player type lookup", {
        draftEmail,
        programId: selectedProgramId,
        primaryDraftId: primaryDraft?.id || null,
        primaryDraftPositionId: primaryDraft?.position_id || null,
        nextPlayerTypeId
      });

      const { data: draftRows, error: draftRowsError } = await supabase
        .from("athlete_swot")
        .select("id, status")
        .eq("program_id", selectedProgramId)
        .is("athlete_id", null)
        .is("position_id", null)
        .ilike("prebuilt_email", draftEmail)
        .order("updated_at", { ascending: false });
      if (draftRowsError) throw draftRowsError;
      console.log("[AdminDashboard] Draft fallback rows", {
        draftEmail,
        programId: selectedProgramId,
        fallbackCount: (draftRows || []).length
      });

      const rowPayload = {
        strengths: swotPayload.strengths,
        weaknesses: swotPayload.weaknesses,
        opportunities: swotPayload.opportunities,
        threats: swotPayload.threats,
        prebuilt_name: draftName || null,
        prebuilt_email: draftEmail,
        updated_at: new Date().toISOString()
      };

      if (primaryDraft?.id) {
        console.log("[AdminDashboard] Updating primary draft row", {
          draftEmail,
          primaryDraftId: primaryDraft.id,
          primaryDraftPositionId: primaryDraft.position_id,
          nextPlayerTypeId
        });
        const { data: updatedDraft, error: updateError } = await supabase
          .from("athlete_swot")
          .update({
            ...rowPayload,
            status: primaryDraft.status || "draft"
          })
          .eq("id", primaryDraft.id)
          .is("athlete_id", null)
          .select("id, prebuilt_name, prebuilt_email, status, created_at, strengths, weaknesses, opportunities, threats, player_type, position_id")
          .single();
        if (updateError) throw updateError;
        upsertDraftEvaluation(updatedDraft);
      } else if (draftRows && draftRows.length > 0) {
        console.log("[AdminDashboard] Updating fallback draft row", {
          draftEmail,
          fallbackId: draftRows[0]?.id || null,
          nextPlayerTypeId
        });
        const row = draftRows[0];
        const { data: updatedDraft, error: updateError } = await supabase
          .from("athlete_swot")
          .update({
            ...rowPayload,
            status: row.status || "draft"
          })
          .eq("id", row.id)
          .is("athlete_id", null)
          .select("id, prebuilt_name, prebuilt_email, status, created_at, strengths, weaknesses, opportunities, threats, player_type, position_id")
          .single();
        if (updateError) throw updateError;
        upsertDraftEvaluation(updatedDraft);
      } else {
        console.log("[AdminDashboard] Inserting draft row", {
          draftEmail,
          programId: selectedProgramId,
          nextPlayerTypeId
        });
        const { data: insertedDraft, error: insertError } = await supabase
          .from("athlete_swot")
          .insert([{
            athlete_id: null,
            program_id: selectedProgramId,
            position_id: null,
            status: "draft",
            ...rowPayload
          }])
          .select("id, prebuilt_name, prebuilt_email, status, created_at, strengths, weaknesses, opportunities, threats, player_type, position_id")
          .single();
        if (insertError) throw insertError;
        upsertDraftEvaluation(insertedDraft);
      }

      setPlayerTypeByAthlete((prev) => ({
        ...prev,
        [athleteKey]: { ...(prev[athleteKey] || {}), player_type: nextPlayerTypeId }
      }));
    } catch (err) {
      console.error("Error assigning draft player type:", err);
      alert("Unable to update draft player type: " + err.message);
    } finally {
      setPlayerTypeSaving((prev) => ({ ...prev, [athleteKey]: false }));
    }
  };

  const handleSaveDraftPlayerType = async (draft) => {
    const drafted = playerTypeDraftByAthlete[getAthletePlayerTypeKey(draft)] || "";
    await handleAssignDraftPlayerType(draft, drafted);
  };

  const handleOpenDraftSwot = (draft) => {
    if (!draft?.id) return;
    navigate(`/admin/swot/${draft.id}`, {
      state: {
        isDraftSwot: true,
        draftSwotId: draft.id,
        prebuiltName: draft.prebuilt_name,
        prebuiltEmail: draft.prebuilt_email,
        sport: currentProgram?.sport,
        programId: selectedProgramId,
        programName: currentProgram?.name
      }
    });
  };

  const handleOpenAthleteSwot = async (athlete) => {
    if (!athlete || athlete.type === "pending") return;
    if (athlete.user_id) {
      navigate(`/admin/swot/${athlete.user_id}`, {
        state: {
          sport: currentProgram?.sport,
          programId: selectedProgramId,
          programName: currentProgram?.name
        }
      });
      return;
    }

    try {
      const draft = athlete.matchedDraft?.id ? athlete.matchedDraft : await ensureDraftSwotForAthlete(athlete);
      if (!draft?.id) {
        alert("This athlete needs an email before a draft SWOT can be created.");
        return;
      }
      navigate(`/admin/swot/${draft.id}`, {
        state: {
          isDraftSwot: true,
          draftSwotId: draft.id,
          prebuiltName: athlete.users?.full_name || athlete.teamsnap_name || draft.prebuilt_name || "",
          prebuiltEmail: athlete.email || athlete.teamsnap_email || draft.prebuilt_email || "",
          sport: currentProgram?.sport,
          programId: selectedProgramId,
          programName: currentProgram?.name
        }
      });
    } catch (err) {
      console.error("Error opening athlete SWOT:", err);
      alert("Unable to open SWOT: " + err.message);
    }
  };

  // --- COACH INVITE LOGIC ---
  const handleInviteCoach = async (e) => {
    e.preventDefault();
    if (!coachInviteForm.email || !selectedProgramId) {
      alert("Please provide an email and ensure a program is selected.");
      return;
    }
    setLoading(true);
    try {
      const token = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2);
      const inviteData = {
        email: coachInviteForm.email,
        program_id: selectedProgramId,
        team_id: coachInviteForm.team_id || selectedTeamId || null,
        role: coachInviteForm.role || "Assistant Coach",
        token: token,
        accepted: null,
        status: 'pending'
      };

      const { data, error } = await supabase.from("coach_invites").insert([inviteData]).select();
      if (error) throw error;

      const newInvite = {
        id: data[0].id,
        name: data[0].email,
        email: data[0].email,
        role: data[0].role,
        teamName: teams.find(t => t.id === data[0].team_id)?.name || "Whole Program",
        status: "Pending",
        type: "pending"
      };

      setCoaches(prev => [newInvite, ...prev]);
      setShowCoachInviteModal(false);
      setCoachInviteForm({ email: "", role: "Assistant Coach", team_id: "" });
      alert("Coach invite sent successfully!");

    } catch (error) {
      console.error("Error sending coach invite:", error);
      alert("Error sending invite: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // --- ATHLETE INVITE LOGIC (UPDATED) ---
  const handleInviteAthlete = async (e) => {
    e.preventDefault();

    // 1. Validation
    if (!athleteInviteForm.email || !selectedProgramId) {
      alert("Please provide an email.");
      return;
    }

    // Determine Team ID (Modal priority -> Filter priority -> Null/Error)
    const teamIdToUse = athleteInviteForm.team_id || selectedTeamId;

    // Optional: Force a team selection if your business logic requires it for athletes
    // if (!teamIdToUse) { alert("Please select a team for the athlete."); return; }

    setLoading(true);

    try {
      // 2. Prepare Payload (Matches player_invites schema)
      const inviteData = {
        program_id: selectedProgramId,
        team_id: teamIdToUse || null,
        email: athleteInviteForm.email,
        status: 'pending'
        // created_at is default now(), id is default uuid_generate_v4()
      };

      // 3. Insert into Supabase
      const { data, error } = await supabase
        .from("player_invites")
        .insert([inviteData])
        .select();

      if (error) throw error;

      // 4. Update Local State (Add 'Pending' card)
      const newAthleteInvite = {
        user_id: data[0].id, // temporary ID for the key
        users: { full_name: data[0].email }, // Show email as name
        email: data[0].email,
        type: "pending", // IMPORTANT: This flag handles the UI difference
        status: "Pending",
        skillAvg: "0.0",
        combineAvg: "0.0",
        overall: 0,
        user_skill_scores: [],
        user_combine_scores: []
      };

      setAthletes(prev => [newAthleteInvite, ...prev]);

      // 5. Cleanup
      setShowAthleteInviteModal(false);
      setAthleteInviteForm({ email: "", team_id: "" });
      alert("Athlete invite sent successfully!");

    } catch (error) {
      console.error("Error sending athlete invite:", error);
      alert("Error sending invite: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrebuildSwot = async (e) => {
    e.preventDefault();

    const name = prebuildSwotForm.name.trim();
    const email = prebuildSwotForm.email.trim();
    const errors = {
      name: name.length >= 2 ? "" : "Name must be at least 2 characters.",
      email: EMAIL_REGEX.test(email) ? "" : "Enter a valid email address."
    };
    setPrebuildSwotErrors(errors);

    if (errors.name || errors.email || !selectedProgramId) return;

    setLoading(true);
    try {
      const { data: existingDraftRows, error: existingDraftError } = await supabase
        .from("athlete_swot")
        .select("id, prebuilt_name, prebuilt_email, status, created_at")
        .eq("program_id", selectedProgramId)
        .is("athlete_id", null)
        .is("position_id", null)
        .ilike("prebuilt_email", email)
        .order("created_at", { ascending: false });
      if (existingDraftError) throw existingDraftError;

      const existingDraft = (existingDraftRows || [])[0] || null;
      let savedDraft = null;

      if (existingDraft?.id) {
        const { data, error } = await supabase
          .from("athlete_swot")
          .update({
            prebuilt_name: name,
            prebuilt_email: email,
            updated_at: new Date().toISOString()
          })
          .eq("id", existingDraft.id)
          .is("athlete_id", null)
          .select("id, prebuilt_name, prebuilt_email, status, created_at")
          .single();
        if (error) throw error;
        savedDraft = data;
      } else {
        const { data, error } = await supabase
          .from("athlete_swot")
          .insert([{
            program_id: selectedProgramId,
            athlete_id: null,
            position_id: null,
            prebuilt_name: name,
            prebuilt_email: email,
            status: "draft"
          }])
          .select("id, prebuilt_name, prebuilt_email, status, created_at")
          .single();
        if (error) throw error;
        savedDraft = data;
      }

      setDraftEvaluations(prev => {
        const next = prev.filter((row) =>
          String(row?.id || "") !== String(savedDraft?.id || "") &&
          normalizeEmail(row?.prebuilt_email) !== normalizeEmail(savedDraft?.prebuilt_email)
        );
        return savedDraft ? [savedDraft, ...next] : next;
      });
      setShowPrebuildSwotModal(false);
      setPrebuildSwotForm({ name: "", email: "" });
      setPrebuildSwotErrors({ name: "", email: "" });
      alert(existingDraft?.id ? "Draft SWOT updated." : "Draft SWOT created.");
    } catch (error) {
      console.error("Error creating draft SWOT:", error);
      alert("Unable to create draft SWOT: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDraftSwot = async (athlete) => {
    if (!athlete?.matchedDraft?.id || !athlete?.user_id) return;
    setLoading(true);
    try {
      const { data: existingPublished, error: existingPublishedError } = await supabase
        .from("athlete_swot")
        .select("id")
        .eq("program_id", selectedProgramId)
        .eq("athlete_id", athlete.user_id)
        .eq("status", "published")
        .maybeSingle();

      if (existingPublishedError) throw existingPublishedError;
      if (existingPublished) {
        alert("This athlete already has a published SWOT. Assignment blocked.");
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from("athlete_swot")
        .update({
          athlete_id: athlete.user_id,
          status: "published",
          prebuilt_name: null,
          prebuilt_email: null
        })
        .eq("id", athlete.matchedDraft.id)
        .is("athlete_id", null);

      if (error) throw error;
      setDraftEvaluations((prev) =>
        prev.filter((row) => String(row?.id || "") !== String(athlete.matchedDraft.id || ""))
      );
      alert("Draft assigned and published.");
    } catch (err) {
      console.error("Error assigning draft SWOT:", err);
      alert("Unable to assign draft SWOT: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishPrebuiltSwot = async (draftId) => {
    if (!draftId) return;
    setLoading(true);
    try {
      const { data: publishedDraft, error } = await supabase
        .from("athlete_swot")
        .update({ status: "published", updated_at: new Date().toISOString() })
        .eq("id", draftId)
        .eq("program_id", selectedProgramId)
        .is("athlete_id", null)
        .eq("status", "draft")
        .select("id, prebuilt_name, prebuilt_email, status, created_at, strengths, weaknesses, opportunities, threats")
        .single();
      if (error) throw error;
      upsertDraftEvaluation(publishedDraft);
      alert("SWOT published and ready for athlete acceptance.");
    } catch (err) {
      console.error("Error publishing prebuilt SWOT:", err);
      alert("Unable to publish SWOT: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openPlayerModal = player => setExpandedPlayer(player);
  const closePlayerModal = () => setExpandedPlayer(null);
  const toggleActionMenu = (athleteId) => {
    setOpenActionMenu((prev) => (prev === athleteId ? null : athleteId));
  };
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (event.target instanceof Node && event.target.closest("[data-action-menu=\"true\"]")) {
        return;
      }
      setOpenActionMenu(null);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);
  const getAthletePlayerTypeKey = (athlete) => String(athlete?.user_id || athlete?.id || "");
  const getAthleteActionKey = (athlete) => String(
    athlete?.user_id ||
    athlete?.id ||
    athlete?.teamsnap_id ||
    athlete?.teamsnap_email ||
    athlete?.email ||
    athlete?.teamsnap_name ||
    ""
  );
  const rosteredByEmail = new Map(
    athletes
      .filter((a) => a.type !== "pending" && a.user_id && a.users?.email)
      .map((a) => [normalizeEmail(a.users.email), a])
  );

  const getSwotCtaLabel = (athlete) => {
    if (!athlete || athlete.type === "pending") return "View SWOT";
    const athleteKey = getAthletePlayerTypeKey(athlete);
    const playerTypeId = playerTypeByAthlete[athleteKey]?.player_type ||
      playerTypeDraftByAthlete[athleteKey] ||
      athlete?.matchedDraft?.player_type ||
      "";
    const positionTypes = Object.values(secondaryPlayerTypeByAthletePosition?.[athleteKey] || {}).filter(Boolean);
    if (!playerTypeId && positionTypes.length === 0) return "Select Player Type";
    return "View SWOT";
  };

  const isSwotCtaDisabled = (athlete) => {
    if (!athlete || athlete.type === "pending") return true;
    const athleteKey = getAthletePlayerTypeKey(athlete);
    const playerTypeId = playerTypeByAthlete[athleteKey]?.player_type ||
      playerTypeDraftByAthlete[athleteKey] ||
      athlete?.matchedDraft?.player_type ||
      "";
    const positionTypes = Object.values(secondaryPlayerTypeByAthletePosition?.[athleteKey] || {}).filter(Boolean);
    return !playerTypeId && positionTypes.length === 0;
  };

  const showInlineLoading = loading && programs.length > 0;

  if (!loading && programs.length === 0) {
    return (
      <div className="pt-6 px-4 pb-20 min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-6">Welcome to Command Center</h1>
          <div onClick={() => navigate('/create-program')} className="bg-white p-8 rounded-2xl shadow hover:shadow-lg cursor-pointer transition">
            <Plus size={48} className="mx-auto text-blue-600 mb-4" />
            <h2 className="text-xl font-bold">Create New Program</h2>
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // 5. RENDER
  // ------------------------------------------------------------------
  return (
    <div className="pt-0 pb-24 md:p-6 md:pb-6 min-h-screen text-gray-900 bg-gray-50">
      <input
        ref={rosterCsvInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={handleRosterCsvUpload}
        disabled={!selectedProgramId || !selectedTeamId || csvImportBusy}
      />

      {/* HEADER CARD */}
      <div className="bg-white md:rounded-2xl shadow-sm border-b md:border border-gray-200 px-4 py-6 md:p-6 mb-6 mt-0 pt-6 md:pt-6">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight">
              {currentProgram?.name || "Admin Dashboard"}
            </h1>
            {showInlineLoading ? (
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Syncing data…
              </div>
            ) : null}
          </div>
          <div className="flex gap-2 w-full md:w-auto mt-2 md:mt-0">
            <button onClick={() => navigate('/create-program')} className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 px-5 rounded-xl shadow-md shadow-blue-200 transition">
              + Create Program
            </button>
            <button onClick={() => navigate('/create-team')} className="flex-1 md:flex-none bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold py-2.5 px-5 rounded-xl shadow-sm transition">
              + Create Team
            </button>
          </div>
        </div>

        {csvImportMessage && (
          <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {csvImportMessage}
          </div>
        )}

        {csvImportError && (
          <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {csvImportError}
          </div>
        )}

        {/* MOBILE NAV */}
        <div className="md:hidden mt-6 border-t border-gray-100 pt-4">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 block">
            Switch Program
          </label>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide snap-x">
            {programs.map((prog) => {
              const isActive = String(selectedProgramId) === String(prog.id);
              return (
                <div key={prog.id} className="relative flex items-center gap-2 snap-start">
                  <button
                    onClick={() => {
                      setSelectedTeamId("");
                      setSelectedProgramId(prog.id);
                    }}
                    className={`
                        whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all border
                        ${isActive ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200" : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"}
                    `}
                  >
                    {prog.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (String(selectedProgramId) !== String(prog.id)) {
                        setSelectedTeamId("");
                        setSelectedProgramId(prog.id);
                      }
                      setOpenProgramActionMenuId((prev) => (prev === prog.id ? null : prog.id));
                    }}
                    aria-label={`Open ${prog.name} actions`}
                    className={`p-2.5 rounded-full border transition ${isActive ? "bg-white text-blue-600 border-blue-200" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                  >
                    <MoreVertical size={16} />
                  </button>
                  {openProgramActionMenuId === prog.id ? (
                    renderTeamActionMenu(() => setOpenProgramActionMenuId(null))
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CONTROLS */}
      {/* Mobile Team Selector */}
      <div className="md:hidden px-4 mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Users className="text-gray-400 w-4 h-4" />
          </div>
          <select
            className="w-full appearance-none bg-white border border-gray-200 text-gray-900 text-sm rounded-xl py-3 pl-10 pr-10 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
            value={selectedTeamId}
            onChange={e => setSelectedTeamId(e.target.value)}
          >
            <option value="">View All Teams</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3.5 text-gray-400 pointer-events-none" size={16} />
        </div>
        {selectedTeamId ? (
          <div className="mt-3 flex justify-end">
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenTeamActionMenu((prev) => !prev)}
                className="p-2.5 bg-white text-gray-600 rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition"
                aria-label="Open team actions"
              >
                <MoreVertical size={18} />
              </button>
              {openTeamActionMenu ? (
                renderTeamActionMenu(() => setOpenTeamActionMenu(false))
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {/* Desktop Filters */}
      <section className="hidden md:grid grid-cols-2 gap-4 mb-8 bg-white p-4 rounded-xl shadow-sm border border-gray-100 mx-6 md:mx-0">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Switch Program</label>
          <select
            className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedProgramId || ""}
            onChange={e => {
              setSelectedTeamId("");
              setSelectedProgramId(e.target.value);
            }}
          >
            {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Filter Team</label>
            <select
              className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedTeamId}
              onChange={e => setSelectedTeamId(e.target.value)}
            >
              <option value="">All Teams</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {selectedTeamId && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenTeamActionMenu((prev) => !prev)}
                title="Team Actions"
                className="mb-[2px] p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-300 transition"
              >
                <MoreVertical size={20} />
              </button>
              {openTeamActionMenu ? (
                renderTeamActionMenu(() => setOpenTeamActionMenu(false))
              ) : null}
            </div>
          )}
        </div>
      </section>

      {/* CONTENT AREA */}
      <div className="px-4 md:px-0">
        {/* COACHES */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Shield size={18} className="text-gray-500" /> Coaching Staff
          </h2>
          <button
            onClick={() => setShowCoachInviteModal(true)}
            className="flex items-center gap-2 text-sm bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition shadow-sm font-medium"
          >
            <Plus size={16} /> Invite
          </button>
        </div>

        {showInlineLoading && coaches.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[...Array(3)].map((_, idx) => (
              <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gray-100" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-100 rounded w-32 mb-2" />
                    <div className="h-3 bg-gray-50 rounded w-40 mb-3" />
                    <div className="flex gap-2">
                      <div className="h-5 w-16 bg-gray-100 rounded" />
                      <div className="h-5 w-24 bg-gray-100 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : coaches.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-xl border border-dashed border-gray-300 mb-8">
            <p className="text-gray-500 text-sm">No coaches assigned.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {coaches.map((coach) => (
              <div key={coach.id} className="relative group bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-start gap-4 hover:shadow-md transition-shadow">
                <button
                  onClick={(e) => { e.stopPropagation(); handleRemoveCoach(coach); }}
                  className="absolute top-3 right-3 text-gray-300 hover:text-red-500 transition opacity-100 md:opacity-0 md:group-hover:opacity-100"
                >
                  <Trash2 size={16} />
                </button>

                <div className={`p-3 rounded-full flex-shrink-0 ${coach.type === 'active' ? 'bg-blue-50 text-blue-600' : 'bg-yellow-50 text-yellow-600'}`}>
                  {coach.type === 'active' ? <User size={20} /> : <Mail size={20} />}
                </div>

                <div className="flex-1 min-w-0 pr-6">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate">{coach.name}</h3>
                    {/* THE COACH PENDING TAG */}
                    {coach.type === 'pending' && (
                      <span className="text-[10px] font-bold uppercase bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Clock size={10} /> Pending
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{coach.email}</p>
                  <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-2">
                    <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{coach.role}</span>
                    <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200 truncate max-w-[120px]">{coach.teamName}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ATHLETES / ROSTER TABLE */}
        <div className="flex justify-between items-center mb-4 border-gray-200">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Users size={18} className="text-gray-500" /> Roster
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setPrebuildSwotErrors({ name: "", email: "" });
                setShowPrebuildSwotModal(true);
              }}
              className="flex items-center gap-2 text-sm bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition shadow-sm font-medium"
            >
              <Plus size={16} /> Pre-build SWOT
            </button>
            <button
              onClick={() => setShowAthleteInviteModal(true)}
              className="flex items-center gap-2 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition shadow-sm font-medium"
            >
              <UserPlus size={16} /> Invite
            </button>
          </div>
        </div>

        <div className="mb-4 inline-flex bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setRosterView("rostered")}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${rosterView === "rostered" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
          >
            Rostered Athletes
          </button>
          <button
            onClick={() => setRosterView("drafted")}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${rosterView === "drafted" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
          >
            Drafted Athletes
          </button>
        </div>

        {showInlineLoading && athletes.length === 0 && draftEvaluations.length === 0 ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gray-100" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-100 rounded w-48 mb-2" />
                    <div className="h-3 bg-gray-50 rounded w-32" />
                  </div>
                  <div className="h-8 w-20 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : rosterView === "rostered" ? (
          <RosteredAthletesTable
            athletes={athletes}
            playerTypeDraftByAthlete={playerTypeDraftByAthlete}
            playerTypeByAthlete={playerTypeByAthlete}
            playerTypeSaving={playerTypeSaving}
            positionSaving={positionSaving}
            playerTypeOptions={playerTypeOptions}
            positionOptions={positionOptions}
            athletePositionIdsByAthlete={athletePositionIdsByAthlete}
            secondaryPlayerTypeByAthletePosition={secondaryPlayerTypeByAthletePosition}
            handleSaveAthletePositions={handleSaveAthletePositions}
            swotStatusByAthlete={swotStatusByAthlete}
            handlePublishAthleteSwot={handlePublishAthleteSwot}
            onPositionsSavedRefresh={handlePositionsSavedRefresh}
            handleSavePlayerTypeWithValue={handleSavePlayerTypeWithValue}
            handleSaveSecondaryPlayerType={handleSaveSecondaryPlayerType}
            openActionMenu={openActionMenu}
            getAthleteActionKey={getAthleteActionKey}
            getAthletePlayerTypeKey={getAthletePlayerTypeKey}
            getAthletePositionLabel={getAthletePositionLabel}
            getRosterImportStatusMeta={getRosterImportStatusMeta}
            getSwotCtaLabel={getSwotCtaLabel}
            isSwotCtaDisabled={isSwotCtaDisabled}
            handlePlayerTypeDraftChange={handlePlayerTypeDraftChange}
            handleSavePlayerType={handleSavePlayerType}
            handleOpenAthleteSwot={handleOpenAthleteSwot}
            handleOpenEditRosterContact={handleOpenEditRosterContact}
            handleRemoveAthlete={handleRemoveAthlete}
            setViewingScores={setViewingScores}
            toggleActionMenu={toggleActionMenu}
          />
        ) : (
          <DraftedAthletesTable
            draftEvaluations={draftEvaluations}
            rosteredByEmail={rosteredByEmail}
            playerTypeDraftByAthlete={playerTypeDraftByAthlete}
            playerTypeByAthlete={playerTypeByAthlete}
            playerTypeSaving={playerTypeSaving}
            playerTypeOptions={playerTypeOptions}
            getAthletePlayerTypeKey={getAthletePlayerTypeKey}
            getAthletePositionLabel={getAthletePositionLabel}
            handlePlayerTypeDraftChange={handlePlayerTypeDraftChange}
            handleSaveDraftPlayerType={handleSaveDraftPlayerType}
            handleOpenDraftSwot={handleOpenDraftSwot}
            handlePublishPrebuiltSwot={handlePublishPrebuiltSwot}
          />
        )}
      </div >

      {/* --- MODALS --- */}
      {
        expandedPlayer && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
              <div className="p-4 border-b flex justify-between bg-gray-50">
                <h2 className="font-bold">{expandedPlayer.users?.full_name}</h2>
                <button onClick={closePlayerModal} className="text-gray-500"><Trash2 size={18} /></button>
              </div>
              <div className="p-5 overflow-y-auto">
                {expandedPlayer.user_skill_scores.map((s, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0"><span>{s.program_goal_skill_scores?.skill_name}</span><span className="font-mono">{s.user_score}</span></div>
                ))}
              </div>
              <div className="p-4 border-t"><button onClick={closePlayerModal} className="w-full py-3 bg-gray-100 font-bold text-gray-700 rounded-lg">Close</button></div>
            </div>
          </div>
        )
      }

      {editingRosterContact ? (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[90] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-gray-900">Edit Contact Info</h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingRosterContact(null);
                  setRosterContactErrors({ name: "", email: "" });
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Name</label>
                <input
                  type="text"
                  value={rosterContactForm.name}
                  onChange={(e) => setRosterContactForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg p-3 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Athlete name"
                />
                {rosterContactErrors.name ? (
                  <p className="mt-1 text-xs text-red-600">{rosterContactErrors.name}</p>
                ) : null}
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                <input
                  type="email"
                  value={rosterContactForm.email}
                  onChange={(e) => setRosterContactForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg p-3 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="athlete@nuviosport.com"
                />
                {rosterContactErrors.email ? (
                  <p className="mt-1 text-xs text-red-600">{rosterContactErrors.email}</p>
                ) : null}
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditingRosterContact(null);
                  setRosterContactErrors({ name: "", email: "" });
                }}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveRosterContact}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                Save Contact
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* --- SCORE BREAKDOWN MODAL --- */}
      {
        viewingScores && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className="p-8 border-b border-gray-100 flex justify-between items-start bg-gray-50/50">
                <div>
                  <h3 className="text-2xl font-black text-gray-900 leading-tight">
                    {viewingScores.type === 'skill'
                      ? 'Skill Matrix'
                      : viewingScores.type === 'combine'
                        ? 'Combine Drills'
                        : 'Program Readiness'}
                  </h3>
                  <p className="text-blue-600 text-xs font-bold uppercase tracking-widest mt-1 flex items-center gap-2">
                    <User size={14} /> {viewingScores.athleteName}
                  </p>
                </div>
                <button onClick={() => setViewingScores(null)} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={24} /></button>
              </div>

              <div className="p-8 space-y-6 max-h-[50vh] overflow-y-auto">
                {viewingScores.type === "overall" ? (
                  <>
                    <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center justify-between">
                      <span className="text-xs font-black text-blue-700 uppercase tracking-wider">Program Readiness</span>
                      <span className="text-lg font-black text-blue-700">{viewingScores.overall ?? "--"}%</span>
                    </div>
                    {[
                      { key: "skill", label: "Skill Scores", rows: viewingScores.skillData || [] },
                      { key: "combine", label: "Combine Scores", rows: viewingScores.combineData || [] }
                    ].map((section) => (
                      <div key={section.key}>
                        <h4 className="text-[11px] font-black text-gray-500 uppercase tracking-widest mb-3">{section.label}</h4>
                        {section.rows.length > 0 ? (
                          <div className="space-y-4">
                            {section.rows.map((item, i) => {
                              const name = section.key === 'skill'
                                ? item.program_goal_skill_scores?.skill_name
                                : item.program_combine_scores?.drill_name;
                              const target = section.key === 'skill'
                                ? item.program_goal_skill_scores?.target_score
                                : item.program_combine_scores?.target_score;
                              const pct = clampReadinessPercent(item.user_score, target).toFixed(0);

                              return (
                                <div key={`${section.key}-${i}`} className="group relative">
                                  <div className="flex justify-between items-center mb-1.5">
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest group-hover:text-blue-600 transition-colors">
                                        {name}
                                      </span>
                                    </div>
                                    <div className="flex items-baseline gap-0.5">
                                      <span className="text-[15px] font-black text-gray-900">{item.user_score}</span>
                                      <span className="text-[10px] font-bold text-gray-300">/{target}</span>
                                    </div>
                                  </div>
                                  <div className="relative w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="absolute top-0 left-0 h-full rounded-full bg-blue-600 transition-all duration-1000 ease-out" style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No data yet.</p>
                        )}
                      </div>
                    ))}
                  </>
                ) : viewingScores.data?.length > 0 ? viewingScores.data.map((item, i) => {
                  const name = viewingScores.type === 'skill' ? item.program_goal_skill_scores?.skill_name : item.program_combine_scores?.drill_name;
                  const target = viewingScores.type === 'skill' ? item.program_goal_skill_scores?.target_score : item.program_combine_scores?.target_score;
                  const pct = clampReadinessPercent(item.user_score, target).toFixed(0);

                  return (
                    <div key={i} className="group relative">
                      {/* Header Info */}
                      <div className="flex justify-between items-center mb-1.5">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest group-hover:text-blue-600 transition-colors">
                            {name}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          {/* Dynamic Status Badge */}
                          <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${Number(pct) >= 100
                            ? 'bg-emerald-50 border-emerald-100 text-emerald-600'
                            : 'bg-gray-50 border-gray-100 text-gray-400'
                            }`}>
                            {Number(pct) >= 100 ? 'Mastered' : 'Developing'}
                          </span>

                          {/* Score Text */}
                          <div className="flex items-baseline gap-0.5">
                            <span className="text-[15px] font-black text-gray-900">{item.user_score}</span>
                            <span className="text-[10px] font-bold text-gray-300">/{target}</span>
                          </div>
                        </div>
                      </div>

                      {/* Refined Progress Bar */}
                      <div className="relative w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        {/* Background Track Glow (Optional - for that premium feel) */}
                        <div
                          className={`absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ease-out ${Number(pct) >= 100
                            ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.4)]'
                            : 'bg-blue-600'
                            }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-center py-10">
                    <BarChart2 className="mx-auto text-gray-200 mb-2" size={48} />
                    <p className="text-gray-400 text-sm font-bold italic">No data yet.</p>
                  </div>
                )}
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-100">
                <button onClick={() => setViewingScores(null)} className="w-full py-4 bg-white border-2 border-gray-100 rounded-2xl text-sm font-black hover:border-blue-600 transition-all">Close Analysis</button>
              </div>
            </div>
          </div>
        )
      }
      {/* INVITE MODAL */}
      {
        (showCoachInviteModal || showAthleteInviteModal || showPrebuildSwotModal) && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <h2 className="text-xl font-bold mb-4">
                {showCoachInviteModal ? 'Invite Coach' : showAthleteInviteModal ? 'Invite Athlete' : 'Create Draft SWOT'}
              </h2>

              <form
                onSubmit={
                  showCoachInviteModal
                    ? handleInviteCoach
                    : showAthleteInviteModal
                      ? handleInviteAthlete
                      : handleCreatePrebuildSwot
                }
                className="space-y-4"
              >

                {/* Email Input */}
                {showPrebuildSwotModal ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        required
                        minLength={2}
                        placeholder="Athlete Name"
                        className={`w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${prebuildSwotErrors.name ? "border-red-400" : "border-gray-300"}`}
                        value={prebuildSwotForm.name}
                        onChange={(e) => setPrebuildSwotForm({ ...prebuildSwotForm, name: e.target.value })}
                      />
                      {prebuildSwotErrors.name && <p className="text-xs text-red-500 mt-1">{prebuildSwotErrors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <input
                        type="email"
                        required
                        placeholder="athlete@nuviosport.com"
                        className={`w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${prebuildSwotErrors.email ? "border-red-400" : "border-gray-300"}`}
                        value={prebuildSwotForm.email}
                        onChange={(e) => setPrebuildSwotForm({ ...prebuildSwotForm, email: e.target.value })}
                      />
                      {prebuildSwotErrors.email && <p className="text-xs text-red-500 mt-1">{prebuildSwotErrors.email}</p>}
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      required
                      placeholder={showCoachInviteModal ? "coach@nuviosport.com" : "athlete@nuviosport.com"}
                      className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={showCoachInviteModal ? coachInviteForm.email : athleteInviteForm.email}
                      onChange={e => showCoachInviteModal
                        ? setCoachInviteForm({ ...coachInviteForm, email: e.target.value })
                        : setAthleteInviteForm({ ...athleteInviteForm, email: e.target.value })
                      }
                    />
                  </div>
                )}

                {/* COACH SPECIFIC FIELDS */}
                {showCoachInviteModal && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Role Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <select
                        className="w-full border border-gray-300 p-3 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                        value={coachInviteForm.role}
                        onChange={(e) => setCoachInviteForm({ ...coachInviteForm, role: e.target.value })}
                      >
                        <option value="Assistant Coach">Assistant Coach</option>
                        <option value="Head Coach">Head Coach</option>
                        <option value="Coordinator">Coordinator</option>
                      </select>
                    </div>

                    {/* Team Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assign Team</label>
                      <select
                        className="w-full border border-gray-300 p-3 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                        value={coachInviteForm.team_id}
                        onChange={(e) => setCoachInviteForm({ ...coachInviteForm, team_id: e.target.value })}
                      >
                        <option value="">Whole Program</option>
                        {teams.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {/* ATHLETE SPECIFIC - Team Selection */}
                {showAthleteInviteModal && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign Team (Optional)</label>
                    <select
                      className="w-full border border-gray-300 p-3 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                      value={athleteInviteForm.team_id}
                      onChange={(e) => setAthleteInviteForm({ ...athleteInviteForm, team_id: e.target.value })}
                    >
                      <option value="">
                        {selectedTeamId ? `Current: ${teams.find(t => t.id === selectedTeamId)?.name}` : "Select a Team"}
                      </option>
                      {teams.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">If no team is selected, athlete will join the program pool.</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCoachInviteModal(false);
                      setShowAthleteInviteModal(false);
                      setShowPrebuildSwotModal(false);
                      setPrebuildSwotForm({ name: "", email: "" });
                      setPrebuildSwotErrors({ name: "", email: "" });
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 font-medium py-2 rounded-lg hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white font-medium py-2 rounded-lg hover:bg-blue-700 transition"
                  >
                    {showCoachInviteModal ? "Send Coach Invite" : showAthleteInviteModal ? "Send Athlete Invite" : "Create Draft SWOT"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
    </div >
  );
}
